import React, { useMemo, useEffect } from "react";
import { useCommonStateFields } from "features/queryReports/hooks/useCommonStateFields";
import { applyCommonOutputs } from "features/queryReports/utils/queryCommon";
import "react-datepicker/dist/react-datepicker.css";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCircleInfo } from "@fortawesome/free-solid-svg-icons";
import { readableRefDate } from "util/medicareRefDate.js";
import classes from "./Reports.module.scss";
import { disableSubmitButton, joinCounties, validateFileName } from "features/queryReports/utils/commonFunctions";
import QueryCreatedModal from "features/queryReports/components/QueryCreatedModal";
import { useSubmitQuery } from "features/queryReports/hooks/useSubmitQuery";
import { placeOfServiceByCountyIDs } from "features/queryReports/utils/reportIds";
import {
  CheckboxInput,
  CountiesSelect,
  FileNameInput,
  RefDateSelect,
} from "features/queryReports/components/FormFields";
import placeOfServiceCountyOutputFields from "features/queryReports/utils/reportOutputFields/PlaceOfServiceCountyOutputFields";

function QueryLogicDescription({ comboRefDate, queryObj }) {
  if (queryObj.parameters.fips.length > 0) {
    return (
      <span>
        I would like to see <strong>organizations</strong> located in the{' '}
        <strong>
          {queryObj.parameters.fips.length > 1 ? 'counties' : 'county'} of{' '}
          {joinCounties(queryObj.parameters.fips, true)}
        </strong>{' '}
        who saw <strong>Medicare and Commercial</strong> patients{' '}
        <span>
          between <strong>{readableRefDate(comboRefDate)}</strong>
        </span>
        .
      </span>
    );
  } else {
    return null;
  }
}

function PlaceOfServiceByCounty({ bridge, payerMixValue, initialParams, showOpenMyQueries }) {
  const {
    categoryId,
    reportId
  } = placeOfServiceByCountyIDs;
  const {
    state: common,
    bind,
    set,
    setOutput,
    resetCommon,
    hydrateFrom,
    selectors
  } = useCommonStateFields({
    overrides: { payerMix: payerMixValue ?? "" }
  });

  const {
    submitQuery,
    waiting,
    showModal,
    modalData,
    closeModal
  } = useSubmitQuery({
    bridge,
    categoryId,
    classes,
    fileName: common.fileName
  });

  const confirmQuerySuccess = type => {
    if (type === 1) {
      closeModal();
      bridge.onMyQueryOpen();
    } else if (type === 2) {
      closeModal();
      bridge.onQuerySubmitted();
    }
  };

  useEffect(() => {
    if (initialParams) {
      hydrateFrom(initialParams);
      // local compute set at initial data 
      // any page-only hydration here as needed
    }
  }, [initialParams]);

  const queryObj = useMemo(() => {
    const retObj = {
      reportId: reportId,
      description: common.fileName,
      parameters: {
        geography_type: 'fipscode',
        fips: common.countiesSelection.length > 0 ? common.countiesSelection : [],
        quarter: common.comboRefDate,
      },
      outputFields: []
    };

    // common outputs
    applyCommonOutputs(retObj, common.outputs, {
      allowList: [
        "stateName",
        "county",
        "icd10Cm",
        "codeServiceLine",
        "codeDescription",
        "address",
        "city",
        "zipCode",
        "source",
        "distinctClaimsCount",
        "distinctPatientsCount",
        "proceduresCount",
        "sumCharges",
        "placeOfService"
      ]
    });

    if (common.outputs.npi) retObj.outputFields.push('Organization NPI');
    if (common.outputs.name) retObj.outputFields.push('Organization Name');
    if (common.outputs.classification) retObj.outputFields.push('Organization Classification');
    if (common.outputs.specialization) retObj.outputFields.push('Organization Specialization');

    return retObj;
  }, [
    common.fileName,
    common.comboRefDate,
    common.countiesSelection,
    common.outputs
  ]);

  function clearFields() {
    resetCommon(); // clears shared fields
    // clear page-only here
  }

  return (
    <div className="container-fluid p-3">
      <div className={classes["report-content-wrap"]}>
        <FileNameInput
          fileName={common.fileName}
          setFileName={v => set("fileName", v)}
          priorQueryParams={common.priorQueryParams}
        />

        <RefDateSelect
          id="comboRefDate"
          label="Commercial + Medicare Reference Date:"
          {...bind.select("comboRefDate")}
          placeholder="Select Commercial + Medicare Reference Date"
          overlayText="Commercial + Medicare reference date"
        />

        <CountiesSelect common={common} queryObj={queryObj} set={set} />


         {common.countiesSelection.length > 0 && (
          <div className="row mb-3">
            <div className={classes["querylogic-wrapper"]}>
              <FontAwesomeIcon
                icon={faCircleInfo}
                className={classes["info-icon"]}
              />
              <div>
                <div className={classes["header"]}>Query Logic</div>
                <div className={classes["body"]}>
                  <QueryLogicDescription
                    comboRefDate={common.comboRefDate}
                    queryObj={queryObj}
                  />
                </div>
              </div>
            </div>
          </div>
        )}

        <div className="row mb-3">
          <div className={classes["querylogic-wrapper"]}>
            <FontAwesomeIcon
              icon={faCircleInfo}
              className={classes["info-icon"]}
            />
            <div>
              <div className={classes["header"]}>Output Fields</div>
               <div className={classes["body"]}>
                  {placeOfServiceCountyOutputFields.map((item, index) => (
                    <CheckboxInput
                      key={item.name || index}
                      item={item}
                      common={common}
                      selectors={selectors}
                      setOutput={setOutput}
                    />
                  ))}
                </div>
            </div>
          </div>
        </div>

        {common.fileName &&
          common.comboRefDate &&
          common.countiesSelection &&
          common.countiesSelection.length > 0 && (
            <div className={`row ${classes["submit-btn-wrap"]}`}>
              <button
                type="button"
                className={`btn btn-primary mr-2 ${classes["submit-btn"]}`}
                disabled={
                  !validateFileName(common.fileName) || disableSubmitButton(common) || common.countiesSelection.length > 10
                }
                onClick={() => submitQuery(queryObj)}
              >
                Submit Query
                {waiting && (
                  <span
                    className="spinner-border spinner-border-sm"
                    role="status"
                    aria-hidden="true"
                  ></span>
                )}
              </button>
              <button
                type="button"
                className={`btn btn-secondary ${classes["cancel-btn"]}`}
                onClick={() => clearFields()}
              >
                Cancel
              </button>
            </div>
          )}

        <QueryCreatedModal
          show={showModal}
          header={modalData.header}
          body={modalData.body}
          classes={classes}
          onClose={() => confirmQuerySuccess(2)}
          onOpenMyQueries={() => confirmQuerySuccess(1)}
          showOpenMyQueries={showOpenMyQueries}
        />
      </div>
    </div>
  );
}

export default PlaceOfServiceByCounty;
